import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/features/auth/AuthProvider";

const schema = z
  .object({
    password: z.string().min(6, "Şifre en az 6 karakter olmalı."),
    confirm: z.string().min(6, "Şifre en az 6 karakter olmalı."),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Şifreler eşleşmiyor.",
    path: ["confirm"],
  });

type ResetValues = z.infer<typeof schema>;

/**
 * SPEC-27 — `/reset-password` landing.
 *
 * The Supabase recovery email links here. supabase-js processes the
 * URL hash on init and the AuthProvider's `onAuthStateChange` issues a
 * `PASSWORD_RECOVERY` session, after which `useAuth().session` is
 * populated. Submitting calls `updatePassword`, which lands a new
 * password on that recovery session. On success we navigate back to
 * `/login` — the recovery session is intentionally short-lived.
 *
 * Mounted OUTSIDE `RequireAuth` so a logged-out user landing here can
 * still complete the flow. If the page is opened without a recovery
 * session (someone typed the URL by hand or the link has expired) we
 * show an inline "invalid link" state instead of a broken form.
 */
export function ResetPasswordPage() {
  const { session, loading, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<ResetValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  });

  // SPEC-27 §4 — if the auth provider has resolved with no session,
  // the recovery hash was never present (direct URL hit) or has
  // already been consumed/expired. Show the inline invalid state so
  // the user does not type into a form that cannot submit.
  const linkInvalid = !loading && !session;

  // Reset the form if we transition into the invalid state, to keep
  // any partially-typed input out of the inline error view.
  useEffect(() => {
    if (linkInvalid) form.reset();
  }, [linkInvalid, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await updatePassword(values.password);
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      navigate("/login");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Şifre güncellenemedi: ${message}`);
    }
  });

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md border-border/60 shadow-xl">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl">Yeni şifre belirleyin</CardTitle>
          <CardDescription>
            {linkInvalid
              ? "Bağlantı geçersiz veya süresi dolmuş."
              : "Hesabınız için yeni bir şifre girin."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div
              className="flex items-center justify-center py-10 text-muted-foreground"
              data-testid="reset-loading"
            >
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Yükleniyor…
            </div>
          ) : linkInvalid ? (
            <InvalidLinkPanel />
          ) : (
            <form
              onSubmit={onSubmit}
              className="space-y-5"
              noValidate
              data-testid="reset-form"
            >
              <div className="space-y-2">
                <Label htmlFor="reset-password">Yeni şifre</Label>
                <PasswordField
                  id="reset-password"
                  autoComplete="new-password"
                  showPassword={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                  register={form.register("password")}
                />
                {form.formState.errors.password ? (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-confirm">Yeni şifre (tekrar)</Label>
                <PasswordField
                  id="reset-confirm"
                  autoComplete="new-password"
                  showPassword={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                  register={form.register("confirm")}
                />
                {form.formState.errors.confirm ? (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.confirm.message}
                  </p>
                ) : null}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
                data-testid="reset-submit"
              >
                {form.formState.isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Şifreyi güncelle
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <Toaster richColors position="bottom-right" />
    </div>
  );
}

function InvalidLinkPanel() {
  return (
    <div
      className="flex flex-col items-center gap-3 py-6 text-center"
      data-testid="reset-invalid"
    >
      <ShieldAlert className="h-8 w-8 text-warning" />
      <p className="text-sm text-muted-foreground">
        Lütfen yeni bir sıfırlama bağlantısı isteyin.
      </p>
      <Button asChild variant="outline" size="sm">
        <Link to="/login">Giriş sayfasına dön</Link>
      </Button>
    </div>
  );
}

export default ResetPasswordPage;
